import React from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  Box,
  Grid,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
} from '@mui/material'

import { autonomyColor } from '../../../../../../global-styles/autonomyColor'
import { FormattedReservationResponse } from '../../useReservation'
import { LinearProgressLoader } from 'shared/LinearProgressLoader'
import { useProgramPricingBlock } from './useProgramPricingBlock'
import { palette } from '../../../../../../global-styles/theme/palette'
import { isGreaterThanZero } from '../../../../../../utils/isGreaterThanZero'
import { formatCents } from '../../../../../../utils/currency-formatter'
import { PricingOption } from '@nxcr-org/web-api-interface/lib/subscription_pb'

interface ProgramPricingBlockProps {
  reservation: FormattedReservationResponse
  isAdmin: boolean
  setReload: () => void
}

// @TODO: Ideally put it this in a Section component, but will need to be adjusted a bit
const useStyles = makeStyles()((theme) => {
  return {
    root: {
      margin: theme.spacing(2, 0, 4, 0),
      width: '100%',
    },
    body: {
      marginTop: 24,
      padding: theme.spacing(3),
      backgroundColor: theme.palette.primary.dark,
      borderRadius: 8,
    },
    loader: {
      backgroundColor: autonomyColor.lightBlue,
    },
    termLength: {
      marginTop: 16,
    },
  }
})

export const ProgramPricingBlock = ({
  reservation,
  isAdmin,
  setReload,
}: ProgramPricingBlockProps) => {
  const { classes } = useStyles()
  const {
    isEdit,
    setIsEdit,
    customPricingOptionId,
    isCustomPriceInput,
    monthlyPayment,
    setMonthly,
    startFee,
    setStartFee,
    handleChange,
    cancelChanges,
    savePricing,
    isValidSubscriptionStatus,
    programPricingOptions,
  } = useProgramPricingBlock(reservation)

  const selectedPricingOption = programPricingOptions?.find((option) => {
    return option.startFee === startFee && option.monthlyRent === monthlyPayment
  })

  const selectedPricingOptionId = isCustomPriceInput
    ? customPricingOptionId
    : selectedPricingOption?.id || 'none'

  const isCustomOption = (option: PricingOption.AsObject) =>
    option.id.startsWith('custom')

  return (
    <Grid className={classes.root}>
      <Grid container size={{xs:12}} justifyContent="space-between">
        <Typography variant="h3">Program Pricing</Typography>

        <Grid>
          {!isEdit && !isValidSubscriptionStatus(reservation) && isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsEdit(true)}
            >
              Edit
            </Button>
          )}

          {isEdit && (
            <Box sx={{ display: 'flex', columnGap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  savePricing.reset()
                  cancelChanges()
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  savePricing
                    .mutateAsync()
                    .then(() => {
                      setIsEdit(false)
                      setReload()
                    })
                    .catch(() => {
                      // Error is visually shown
                    })
                }}
                disabled={!isGreaterThanZero(monthlyPayment)}
              >
                Save
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      {savePricing.isPending && <LinearProgressLoader />}
      <Grid className={classes.body}>
        <Grid container direction="column" spacing={1}>
          <Grid>
            {!isValidSubscriptionStatus(reservation) && isAdmin && !isEdit && (
              <Typography>
                Select <b>Edit</b> to update this pricing.
              </Typography>
            )}
          </Grid>
            <Grid>
              <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                {isEdit ? (
                  <>
                    <Typography>
                      Select <b>new</b> monthly payment and start fee (legacy
                      pricing not shown)
                    </Typography>
                    <Select
                      displayEmpty
                      id="pricing-options-select"
                      value={selectedPricingOptionId}
                      label="pricingOptions"
                      onChange={(e) => handleChange(e)}
                    >
                      <MenuItem disabled value="none">
                        Select a pricing option
                      </MenuItem>
                      <MenuItem value={customPricingOptionId}>
                        Custom input pricing
                      </MenuItem>
                      {programPricingOptions.map((option) => {
                        return (
                          <MenuItem value={option.id} key={option.id}>
                            {`${formatCents(
                              option.monthlyRent
                            )}/ mo and ${formatCents(
                              option.startFee
                            )} / start fee`}
                            {isCustomOption(option) ? ' (custom)' : ''}
                          </MenuItem>
                        )
                      })}
                    </Select>
                    {isCustomPriceInput && (
                      <Grid container spacing={1} sx={{ marginTop: 1 }}>
                        <Grid>
                          <TextField
                            type="number"
                            label="Monthly Rent"
                            value={monthlyPayment / 100}
                            onChange={(event) => {
                              const dollars = Number(event.target.value)
                              if (Number.isNaN(dollars)) {
                                setMonthly(0)
                                return
                              }
                              setMonthly(Math.round(dollars * 100))
                            }}
                            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                          />
                        </Grid>
                        <Grid>
                          <TextField
                            type="number"
                            label="Start Fee"
                            value={startFee / 100}
                            onChange={(event) => {
                              const dollars = Number(event.target.value)
                              if (Number.isNaN(dollars)) {
                                setStartFee(0)
                                return
                              }
                              setStartFee(Math.round(dollars * 100))
                            }}
                            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="caption">
                      Monthly Payment and Start Fee
                    </Typography>
                    <Typography>
                      {' '}
                      {formatCents(monthlyPayment)}/mo and{' '}
                      {formatCents(startFee)} start fee
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
            <Grid>
              {savePricing.isSuccess && (
                <Typography color={palette.text.success}>
                  Changes saved successfully
                </Typography>
              )}
              {savePricing.isError && (
                <Typography color={palette.text.error}>
                  {savePricing.error}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
  )
}
